import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, CubeIcon } from '@heroicons/react/24/outline';
import { Product } from '../types';
import { ApiService } from '../services/api';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      loadProduct(parseInt(id));
    }
  }, [id]);

  const loadProduct = async (productId: number) => {
    try {
      setIsLoading(true);
      const response = await ApiService.getProduct(productId);
      if (response.success && response.data) {
        setProduct(response.data);
      }
    } catch (error) {
      console.error('Failed to load product:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/products/${id}` } } });
      return;
    }

    try {
      setIsAddingToCart(true);
      const success = await addToCart(product, quantity);
      if (success) {
        // 성공 메시지 표시 또는 장바구니 페이지로 이동
        alert('장바구니에 추가되었습니다!');
      }
    } catch (error) {
      console.error('Failed to add to cart:', error);
      alert('장바구니에 추가하는 중 오류가 발생했습니다.');
    } finally {
      setIsAddingToCart(false);
    }
  };

  const formatPrice = (cashPrice: number, goldPrice: number) => {
    const parts = [];
    if (cashPrice > 0) parts.push(`${cashPrice.toLocaleString()} CP`);
    if (goldPrice > 0) parts.push(`${goldPrice.toLocaleString()} GP`);
    return parts.join(' + ') || '무료';
  };

  const getPaymentTypeBadge = (type: string) => {
    switch (type) {
      case 'cash_only':
        return <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded">Cash Point 전용</span>;
      case 'gold_only':
        return <span className="bg-yellow-100 text-yellow-800 text-sm px-3 py-1 rounded">Gold Point 전용</span>;
      case 'mixed':
        return <span className="bg-green-100 text-green-800 text-sm px-3 py-1 rounded">혼합 결제</span>;
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-gray-300 h-96 rounded"></div>
            <div className="space-y-4">
              <div className="bg-gray-300 h-8 rounded"></div>
              <div className="bg-gray-300 h-4 rounded w-3/4"></div>
              <div className="bg-gray-300 h-6 rounded w-1/2"></div>
              <div className="bg-gray-300 h-20 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <CubeIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">상품을 찾을 수 없습니다</h2>
        <p className="text-gray-600">요청하신 상품이 존재하지 않거나 삭제되었습니다.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Image */}
        <div className="aspect-w-1 aspect-h-1">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-full h-96 object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-96 bg-gray-200 rounded-lg flex items-center justify-center">
              <CubeIcon className="h-24 w-24 text-gray-400" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                {product.category}
              </span>
              {getPaymentTypeBadge(product.paymentType)}
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>
            <p className="text-2xl font-semibold text-kahan-primary mb-4">
              {formatPrice(product.cashPointPrice, product.goldPointPrice)}
            </p>
          </div>

          {product.description && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">상품 설명</h3>
              <p className="text-gray-600 whitespace-pre-wrap">{product.description}</p>
            </div>
          )}

          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center space-x-4 mb-4">
              <label htmlFor="quantity" className="text-sm font-medium text-gray-700">
                수량:
              </label>
              <select
                id="quantity"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 focus:ring-kahan-primary focus:border-kahan-primary"
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleAddToCart}
                disabled={isAddingToCart || !product.isActive}
                className="w-full bg-kahan-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-kahan-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <ShoppingCartIcon className="h-5 w-5 mr-2" />
                {isAddingToCart ? '추가 중...' : '장바구니에 추가'}
              </button>

              {!isAuthenticated && (
                <p className="text-sm text-gray-600 text-center">
                  구매하려면 <button onClick={() => navigate('/login')} className="text-kahan-primary font-medium hover:text-blue-700">로그인</button>이 필요합니다.
                </p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">소스:</span>
                <span className="ml-2 font-medium">
                  {product.sourceType === 'admin' ? '관리자 등록' :
                   product.sourceType === 'crawled' ? '자동 수집' : '바우처'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">상태:</span>
                <span className={`ml-2 font-medium ${product.isActive ? 'text-green-600' : 'text-red-600'}`}>
                  {product.isActive ? '판매중' : '판매중지'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;